/**
 * GRN (Goods Receipt Note) Management Page
 * AS9100D-Compliant Receiving & QC Workflow
 * 
 * Features:
 * - Create and manage GRNs
 * - Add receipt lines with lot tracking
 * - FOD verification checkpoint
 * - QC inspection queue
 * - Quarantine management
 * - Enterprise Dashboard (Phase 2)
 * 
 * AS9100D Clauses:
 * - 8.4.2: External Provider Control
 * - 8.5.2: Identification and Traceability
 * - 8.6: Release of Products
 * - 8.7: Control of Nonconforming Outputs
 * 
 * @version 2.0.0 (Enterprise Upgrade)
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Package,
  Plus,
  Search,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  FileText,
  Truck,
  ClipboardCheck,
  Shield,
  Box,
  AlertCircle,
  Check,
  X,
  Calendar,
  Building2,
  Hash,
  Upload,
  File,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { formatIST } from '../utils/timezone';
import api from '../services/api';
import { toast } from 'sonner';
import InventoryLayout from '../components/layouts/InventoryLayout';
import CarrierCombobox from '../components/CarrierCombobox';
import { ESignatureDialog } from '../components/ESignatureDialog';

// Import extracted GRN components (Phase 2, 3 & 4 - Enterprise Upgrade)
import { 
  GRNDashboardHeader, 
  GRNFilters, 
  GRNTableView, 
  ViewToggle, 
  getSavedViewMode 
} from '../components/grn';

// Status badge colors
const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700 border-gray-200',
  RECEIVED: 'bg-blue-100 text-blue-700 border-blue-200',
  INSPECTING: 'bg-amber-100 text-amber-700 border-amber-200',
  COMPLETED: 'bg-green-100 text-green-700 border-green-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200',
  PENDING: 'bg-gray-100 text-gray-700 border-gray-200',
  ACCEPTED: 'bg-green-100 text-green-700 border-green-200',
  REJECTED: 'bg-red-100 text-red-700 border-red-200',
  PARTIAL: 'bg-amber-100 text-amber-700 border-amber-200',
  IN_PROGRESS: 'bg-blue-100 text-blue-700 border-blue-200',
};

const STATUS_ICONS = {
  DRAFT: Clock,
  RECEIVED: Package,
  INSPECTING: ClipboardCheck,
  COMPLETED: CheckCircle,
  CANCELLED: XCircle,
  PENDING: Clock,
  ACCEPTED: CheckCircle,
  REJECTED: XCircle,
  PARTIAL: AlertTriangle,
  IN_PROGRESS: Loader2,
};

// Helper function to calculate days to expiry
const getDaysToExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  expiry.setHours(0, 0, 0, 0);
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Helper function to get expiry badge color
const getExpiryBadgeColor = (daysToExpiry) => {
  if (daysToExpiry === null) return 'bg-gray-100 text-gray-600 border-gray-200';
  if (daysToExpiry < 0) return 'bg-red-100 text-red-700 border-red-300';
  if (daysToExpiry <= 30) return 'bg-red-100 text-red-700 border-red-300';
  if (daysToExpiry <= 90) return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-green-100 text-green-700 border-green-300';
};

/**
 * Compliance Badge Component
 */
const ComplianceBadge = ({ clause, description }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
          <Shield className="w-3 h-3 mr-1" />
          AS9100D {clause}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <p className="text-sm">{description}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/**
 * Status Badge Component
 */
const StatusBadge = ({ status }) => {
  const Icon = STATUS_ICONS[status] || Clock;
  return (
    <Badge className={`${STATUS_COLORS[status] || STATUS_COLORS.DRAFT} border`}>
      <Icon className={`w-3 h-3 mr-1 ${status === 'IN_PROGRESS' ? 'animate-spin' : ''}`} />
      {status?.replace(/_/g, ' ')}
    </Badge>
  );
};

/**
 * GRN Summary Card
 */
const GRNCard = ({ grn, onClick }) => {
  const StatusIcon = STATUS_ICONS[grn.status] || Clock;
  
  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-all duration-200 hover:border-blue-300"
      onClick={() => onClick(grn)}
      data-testid={`grn-card-${grn.grn_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-semibold text-[#101957]">{grn.grn_number}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {formatIST(grn.receipt_date)}
            </p>
          </div>
          <StatusBadge status={grn.status} />
        </div>
        
        <div className="space-y-2 text-sm">
          {grn.po_number && (
            <div className="flex items-center text-gray-600">
              <FileText className="w-4 h-4 mr-2 text-gray-400" />
              <span>PO: {grn.po_number}</span>
            </div>
          )}
          {grn.supplier_name && (
            <div className="flex items-center text-gray-600">
              <Building2 className="w-4 h-4 mr-2 text-gray-400" />
              <span className="truncate">{grn.supplier_name}</span>
            </div>
          )}
          {grn.tracking_number && (
            <div className="flex items-center text-gray-600">
              <Truck className="w-4 h-4 mr-2 text-gray-400" />
              <span className="truncate">{grn.carrier}: {grn.tracking_number}</span>
            </div>
          )}
        </div>
        
        <Separator className="my-3" />
        
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{grn.line_count || 0} line(s)</span>
          <div className="flex items-center gap-2">
            {grn.lines_accepted > 0 && (
              <span className="text-green-600">✓ {grn.lines_accepted}</span>
            )}
            {grn.lines_inspecting > 0 && (
              <span className="text-amber-600">◐ {grn.lines_inspecting}</span>
            )}
            {grn.lines_rejected > 0 && (
              <span className="text-red-600">✗ {grn.lines_rejected}</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Create GRN Dialog
 */
const CreateGRNDialog = ({ open, onOpenChange, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    po_number: '',
    supplier_name: '',
    packing_slip_number: '',
    carrier: '',
    tracking_number: '',
    receiving_dock: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.post('/api/v1/inventory/v2/grn', formData);
      toast.success('GRN created successfully');
      onCreated(response.data);
      onOpenChange(false);
      setFormData({
        po_number: '',
        supplier_name: '',
        packing_slip_number: '',
        carrier: '',
        tracking_number: '',
        receiving_dock: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create GRN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            Create Goods Receipt Note
          </DialogTitle>
          <DialogDescription>
            Start a new receiving document. Add lines after creation.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="po_number">PO Number</Label>
              <Input
                id="po_number"
                placeholder="PO-2026-00001"
                value={formData.po_number}
                onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                data-testid="grn-po-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier_name">Supplier</Label>
              <Input
                id="supplier_name"
                placeholder="Supplier name"
                value={formData.supplier_name}
                onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                data-testid="grn-supplier-input"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="packing_slip">Packing Slip #</Label>
              <Input
                id="packing_slip"
                placeholder="PS-12345"
                value={formData.packing_slip_number}
                onChange={(e) => setFormData({ ...formData, packing_slip_number: e.target.value })}
                data-testid="grn-packing-slip-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="receiving_dock">Receiving Dock</Label>
              <Input
                id="receiving_dock"
                placeholder="Dock A"
                value={formData.receiving_dock}
                onChange={(e) => setFormData({ ...formData, receiving_dock: e.target.value })}
              />
            </div>
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="carrier">Carrier</Label>
              <CarrierCombobox
                value={formData.carrier}
                onChange={(value) => setFormData({ ...formData, carrier: value })}
                placeholder="Search or add carrier..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tracking">Tracking Number</Label>
              <Input
                id="tracking"
                placeholder="1234567890"
                value={formData.tracking_number}
                onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Optional notes about this receipt..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="grn-create-submit">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create GRN
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Add GRN Line Dialog
 */
const AddLineDialog = ({ open, onOpenChange, grnId, onAdded }) => {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [partSearch, setPartSearch] = useState('');
  const [partResults, setPartResults] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [locations, setLocations] = useState([]);
  const [cocFile, setCocFile] = useState(null);
  const [cocUploadLater, setCocUploadLater] = useState(false);
  const [formData, setFormData] = useState({
    quantity_ordered: '',
    quantity_received: '',
    supplier_lot_number: '',
    manufacture_date: '',
    expiry_date: '',
    put_away_location_id: '',
    notes: ''
  });

  // Search parts - multi-field search
  useEffect(() => {
    const searchParts = async () => {
      if (partSearch.length < 2) {
        setPartResults([]);
        return;
      }
      
      setSearching(true);
      try {
        // Use 'q' parameter for multi-field search (HPN, description, make, model, category)
        const response = await api.get(`/api/v1/parts/search?q=${encodeURIComponent(partSearch)}&limit=15`);
        setPartResults(response.data.items || response.data.data || []);
      } catch (error) {
        console.error('Error searching parts:', error);
      } finally {
        setSearching(false);
      }
    };
    
    const debounce = setTimeout(searchParts, 300);
    return () => clearTimeout(debounce);
  }, [partSearch]);

  // Load locations
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await api.get('/api/v1/inventory/v2/locations?active_only=true&page_size=100');
        setLocations(response.data.items || []);
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };
    
    if (open) {
      loadLocations();
    }
  }, [open]);

  const handleSelectPart = (part) => {
    setSelectedPart(part);
    setPartSearch(part.hpn_display || part.hpn_compact);
    setPartResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPart) {
      toast.error('Please select a part');
      return;
    }
    
    if (!formData.quantity_received || parseFloat(formData.quantity_received) <= 0) {
      toast.error('Please enter quantity received');
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        part_id: selectedPart.hpn_compact,
        quantity_ordered: parseFloat(formData.quantity_ordered) || 0,
        quantity_received: parseFloat(formData.quantity_received),
        supplier_lot_number: formData.supplier_lot_number || null,
        manufacture_date: formData.manufacture_date || null,
        expiry_date: formData.expiry_date || null,
        put_away_location_id: formData.put_away_location_id || null,
        notes: formData.notes || null,
        coc_pending: cocUploadLater && !cocFile
      };
      
      const response = await api.post(`/api/v1/inventory/v2/grn/${grnId}/lines`, payload);
      
      // Upload CoC file if provided
      if (cocFile && response.data?.line_id) {
        try {
          const cocFormData = new FormData();
          cocFormData.append('file', cocFile);
          cocFormData.append('line_id', response.data.line_id);
          await api.post(`/api/v1/inventory/v2/grn/${grnId}/lines/${response.data.line_id}/coc`, cocFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Line added with CoC document');
        } catch (cocError) {
          console.error('CoC upload failed:', cocError);
          toast.warning('Line added but CoC upload failed. You can upload later.');
        }
      } else {
        toast.success('Line added successfully');
      }
      
      onAdded(response.data);
      
      // Reset form
      setSelectedPart(null);
      setPartSearch('');
      setCocFile(null);
      setCocUploadLater(false);
      setFormData({
        quantity_ordered: '',
        quantity_received: '',
        supplier_lot_number: '',
        manufacture_date: '',
        expiry_date: '',
        put_away_location_id: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add line');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Box className="w-5 h-5 text-blue-600" />
            Add Receipt Line
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            Add a part to this GRN. A lot will be auto-created.
            <ComplianceBadge clause="8.5.2" description="Identification and Traceability - Lot auto-creation" />
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Part Search */}
          <div className="space-y-2">
            <Label>Part Number *</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by part number, description, make, model..."
                value={partSearch}
                onChange={(e) => {
                  setPartSearch(e.target.value);
                  setSelectedPart(null);
                }}
                className="pl-10"
                data-testid="grn-line-part-search"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
              )}
            </div>
            
            {/* Part results dropdown - Enhanced with rich info */}
            {partResults.length > 0 && !selectedPart && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-72 overflow-y-auto">
                <div className="px-3 py-2 bg-gray-50 border-b text-xs text-gray-500">
                  {partResults.length} result{partResults.length !== 1 ? 's' : ''} found
                </div>
                {partResults.map((part, index) => (
                  <button
                    key={part.hpn_compact}
                    type="button"
                    className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors ${index !== partResults.length - 1 ? 'border-b border-gray-100' : ''}`}
                    onClick={() => handleSelectPart(part)}
                    data-testid={`part-result-${part.hpn_compact}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {/* Part Number */}
                        <p className="font-semibold text-gray-900">{part.hpn_display || part.hpn_compact}</p>
                        {/* Description */}
                        <p className="text-sm text-gray-600 truncate mt-0.5">{part.description}</p>
                        {/* Make / Model / Category row */}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs">
                          {part.manufacturer_name && (
                            <span className="flex items-center gap-1 text-gray-500">
                              <Building2 className="w-3 h-3" />
                              <span className="font-medium">{part.manufacturer_name}</span>
                            </span>
                          )}
                          {part.mpn && (
                            <span className="flex items-center gap-1 text-gray-500">
                              <Hash className="w-3 h-3" />
                              {part.mpn}
                            </span>
                          )}
                          {part.category && (
                            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {part.category}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* UOM Badge */}
                      <Badge variant="outline" className="text-xs shrink-0 bg-white">
                        {part.uom || 'EA'}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {/* No results message */}
            {partSearch.length >= 2 && !searching && partResults.length === 0 && !selectedPart && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-4 text-center">
                <p className="text-gray-500 text-sm">No parts found matching &quot;{partSearch}&quot;</p>
                <p className="text-gray-400 text-xs mt-1">Try searching by part number, description, manufacturer, or model</p>
              </div>
            )}
            
            {/* Selected part display - Enhanced */}
            {selectedPart && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-blue-900 text-lg">{selectedPart.hpn_display}</p>
                    <p className="text-sm text-blue-700 mt-1">{selectedPart.description}</p>
                    
                    {/* Make / Model / Category */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
                      {selectedPart.manufacturer_name && (
                        <span className="flex items-center gap-1.5 text-blue-600">
                          <Building2 className="w-4 h-4" />
                          <span className="font-medium">{selectedPart.manufacturer_name}</span>
                        </span>
                      )}
                      {selectedPart.mpn && (
                        <span className="flex items-center gap-1.5 text-blue-600">
                          <Hash className="w-4 h-4" />
                          {selectedPart.mpn}
                        </span>
                      )}
                      {selectedPart.category && (
                        <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                          {selectedPart.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                    onClick={() => {
                      setSelectedPart(null);
                      setPartSearch('');
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Compliance indicators */}
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-blue-200 text-xs text-blue-600">
                  {selectedPart.requires_inspection && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded">
                      <ClipboardCheck className="w-3 h-3" />
                      Requires QC
                    </span>
                  )}
                  {selectedPart.requires_coc && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded">
                      <FileText className="w-3 h-3" />
                      CoC Required
                    </span>
                  )}
                  {selectedPart.shelf_life_days && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded">
                      <Calendar className="w-3 h-3" />
                      Shelf Life: {selectedPart.shelf_life_days}d
                    </span>
                  )}
                  <Badge variant="outline" className="bg-white text-blue-700 border-blue-300">
                    UOM: {selectedPart.uom || 'EA'}
                  </Badge>
                </div>
              </div>
            )}
          </div>
          
          <Separator />
          
          {/* Quantities */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="qty_ordered">Qty Ordered</Label>
              <Input
                id="qty_ordered"
                type="number"
                min="0"
                step="0.01"
                placeholder="0"
                value={formData.quantity_ordered}
                onChange={(e) => setFormData({ ...formData, quantity_ordered: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="qty_received">Qty Received *</Label>
              <Input
                id="qty_received"
                type="number"
                min="0"
                step="0.01"
                placeholder="0"
                value={formData.quantity_received}
                onChange={(e) => setFormData({ ...formData, quantity_received: e.target.value })}
                required
                data-testid="grn-line-qty-input"
              />
            </div>
          </div>
          
          {/* Lot Info */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="supplier_lot">Supplier Lot #</Label>
              <Input
                id="supplier_lot"
                placeholder="MFG-LOT-001"
                value={formData.supplier_lot_number}
                onChange={(e) => setFormData({ ...formData, supplier_lot_number: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfg_date">Manufacture Date</Label>
              <Input
                id="mfg_date"
                type="date"
                value={formData.manufacture_date}
                onChange={(e) => setFormData({ ...formData, manufacture_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="exp_date">Expiry Date</Label>
              <Input
                id="exp_date"
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
              />
            </div>
          </div>
          
          {/* Put-away Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Put-away Location</Label>
            <Select
              value={formData.put_away_location_id}
              onValueChange={(value) => setFormData({ ...formData, put_away_location_id: value })}
            >
              <SelectTrigger id="location">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>
                    {loc.code} - {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Certificate of Conformance (CoC) */}
          <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <Label className="text-blue-800 font-medium">Certificate of Conformance (CoC)</Label>
              {selectedPart?.requires_coc && (
                <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300">
                  Required
                </Badge>
              )}
            </div>
            
            {!cocFile ? (
              <div className="space-y-3">
                <div 
                  className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => document.getElementById('coc-file-input').click()}
                >
                  <Upload className="w-8 h-8 mx-auto text-blue-400 mb-2" />
                  <p className="text-sm text-blue-700">Click to upload CoC document</p>
                  <p className="text-xs text-blue-500 mt-1">PDF, JPG, PNG (max 10MB)</p>
                </div>
                <input
                  id="coc-file-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error('File size must be less than 10MB');
                        return;
                      }
                      setCocFile(file);
                      setCocUploadLater(false);
                    }
                  }}
                  data-testid="coc-file-input"
                />
                
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="coc-later" 
                    checked={cocUploadLater}
                    onCheckedChange={(checked) => setCocUploadLater(checked)}
                  />
                  <Label htmlFor="coc-later" className="text-sm text-blue-700 cursor-pointer">
                    Upload later (mark as pending)
                  </Label>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <File className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{cocFile.name}</p>
                    <p className="text-xs text-gray-500">{(cocFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setCocFile(null)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="line_notes">Notes</Label>
            <Textarea
              id="line_notes"
              placeholder="Optional notes..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !selectedPart} data-testid="grn-line-add-submit">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Line
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * FOD Verification Dialog
 */
const FODVerificationDialog = ({ open, onOpenChange, grn, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [fodChecks, setFodChecks] = useState({
    packaging_intact: false,
    no_visible_contamination: false,
    no_loose_hardware: false,
    desiccant_indicator_ok: false
  });

  const allChecked = Object.values(fodChecks).every(v => v);

  const handleComplete = async () => {
    setLoading(true);
    
    try {
      const response = await api.post(`/api/v1/inventory/v2/grn/${grn.grn_id}/receive`, {
        fod_verified: true
      });
      toast.success('GRN receipt completed - Lines routed to QC');
      onComplete(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete receipt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" />
            FOD Verification Checkpoint
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            Complete FOD checks before releasing to QC inspection.
            <ComplianceBadge clause="8.1" description="FOD Prevention - Operational Planning" />
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              All FOD checks must pass before completing receipt. Lines requiring inspection will be routed to the QC queue.
            </p>
          </div>
          
          <div className="space-y-3">
            {[
              { key: 'packaging_intact', label: 'Packaging is intact and undamaged' },
              { key: 'no_visible_contamination', label: 'No visible contamination or foreign material' },
              { key: 'no_loose_hardware', label: 'No loose hardware or debris in packaging' },
              { key: 'desiccant_indicator_ok', label: 'Desiccant/humidity indicators OK (if applicable)' }
            ].map((check) => (
              <div key={check.key} className="flex items-center space-x-3">
                <Checkbox
                  id={check.key}
                  checked={fodChecks[check.key]}
                  onCheckedChange={(checked) => 
                    setFodChecks({ ...fodChecks, [check.key]: checked })
                  }
                />
                <Label htmlFor={check.key} className="cursor-pointer">
                  {check.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleComplete} 
            disabled={loading || !allChecked}
            className="bg-green-600 hover:bg-green-700"
            data-testid="fod-complete-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Complete Receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Complete Inspection Dialog
 */
const CompleteInspectionDialog = ({ open, onOpenChange, inspection, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('PASS');
  const [qtyAccepted, setQtyAccepted] = useState(inspection?.quantity_to_inspect || 0);
  const [qtyRejected, setQtyRejected] = useState(0);
  const [notes, setNotes] = useState('');
  const [cocVerified, setCocVerified] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showESignature, setShowESignature] = useState(false);

  // Rejection reason options for AS9100D compliance
  const REJECTION_REASONS = [
    { value: 'DIMENSIONAL_FAIL', label: 'Dimensional Non-Conformance' },
    { value: 'VISUAL_DEFECT', label: 'Visual/Surface Defect' },
    { value: 'MATERIAL_DEFECT', label: 'Material Non-Conformance' },
    { value: 'DOCUMENTATION_FAIL', label: 'Documentation Issue' },
    { value: 'CONTAMINATION', label: 'Contamination/FOD' },
    { value: 'FUNCTIONAL_FAIL', label: 'Functional Test Failure' },
    { value: 'COUNTERFEIT_SUSPECT', label: 'Suspected Counterfeit' },
    { value: 'SAFETY_ISSUE', label: 'Safety Concern' },
    { value: 'OTHER', label: 'Other (Specify in Notes)' },
  ];

  useEffect(() => {
    if (inspection) {
      setQtyAccepted(inspection.quantity_to_inspect || 0);
      setQtyRejected(0);
      setRejectionReason('');
    }
  }, [inspection]);

  useEffect(() => {
    if (result === 'PASS') {
      setQtyAccepted(inspection?.quantity_to_inspect || 0);
      setQtyRejected(0);
      setRejectionReason('');
    } else if (result === 'FAIL') {
      setQtyAccepted(0);
      setQtyRejected(inspection?.quantity_to_inspect || 0);
    }
  }, [result, inspection]);

  const validateAndProceed = () => {
    const total = parseFloat(qtyAccepted) + parseFloat(qtyRejected);
    if (total !== inspection.quantity_to_inspect) {
      toast.error(`Accepted + Rejected must equal ${inspection.quantity_to_inspect}`);
      return;
    }
    
    if (inspection.coc_required && !cocVerified) {
      toast.error('CoC verification is required for this part');
      return;
    }

    // Require rejection reason for FAIL/PARTIAL
    if ((result === 'FAIL' || (result === 'PARTIAL' && qtyRejected > 0)) && !rejectionReason) {
      toast.error('Please select a rejection reason');
      return;
    }

    // Show e-signature dialog
    setShowESignature(true);
  };

  const handleESignatureConfirm = async (signatureData) => {
    setLoading(true);
    
    try {
      const response = await api.post(`/api/v1/inventory/v2/inspections/${inspection.inspection_id}/complete`, {
        result,
        quantity_accepted: parseFloat(qtyAccepted),
        quantity_rejected: parseFloat(qtyRejected),
        notes,
        coc_verified: cocVerified,
        rejection_reason: rejectionReason || null,
        e_signature: {
          signed_by_id: signatureData.user_id,
          signed_by: signatureData.user_name,
          signed_at: signatureData.timestamp,
          acknowledged: signatureData.acknowledged,
          signature_meaning: `QC Inspection completed with result: ${result}`
        }
      });
      
      // Show appropriate message based on result
      if (result === 'PASS') {
        toast.success('Inspection PASSED - Lot released to stock');
      } else if (result === 'FAIL') {
        toast.warning('Inspection FAILED - Lot quarantined, NCR created');
      } else {
        toast.info(`Inspection PARTIAL - ${qtyAccepted} accepted, ${qtyRejected} quarantined`);
      }
      
      onComplete(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete inspection');
    } finally {
      setLoading(false);
      setShowESignature(false);
    }
  };

  if (!inspection) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-blue-600" />
            Complete Inspection
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            {inspection.inspection_number} - {inspection.hpn_display}
            <ComplianceBadge clause="8.4.2" description="Verification of Externally Provided Products" />
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Part Info */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-gray-500">Part</p>
                <p className="font-medium">{inspection.hpn_display}</p>
              </div>
              <div>
                <p className="text-gray-500">Quantity</p>
                <p className="font-medium">{inspection.quantity_to_inspect} {inspection.uom || 'EA'}</p>
              </div>
              <div>
                <p className="text-gray-500">Lot</p>
                <p className="font-medium">{inspection.lot_number}</p>
              </div>
              <div>
                <p className="text-gray-500">Supplier</p>
                <p className="font-medium">{inspection.supplier_name || 'N/A'}</p>
              </div>
            </div>
          </div>
          
          {/* Result Selection */}
          <div className="space-y-2">
            <Label>Inspection Result *</Label>
            <div className="flex gap-2">
              {['PASS', 'PARTIAL', 'FAIL'].map((r) => (
                <Button
                  key={r}
                  type="button"
                  variant={result === r ? 'default' : 'outline'}
                  className={result === r ? (
                    r === 'PASS' ? 'bg-green-600 hover:bg-green-700' :
                    r === 'FAIL' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-amber-600 hover:bg-amber-700'
                  ) : ''}
                  onClick={() => setResult(r)}
                >
                  {r === 'PASS' && <CheckCircle className="w-4 h-4 mr-1" />}
                  {r === 'FAIL' && <XCircle className="w-4 h-4 mr-1" />}
                  {r === 'PARTIAL' && <AlertTriangle className="w-4 h-4 mr-1" />}
                  {r}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Quantities */}
          {result === 'PARTIAL' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="qty_accept">Qty Accepted</Label>
                <Input
                  id="qty_accept"
                  type="number"
                  min="0"
                  max={inspection.quantity_to_inspect}
                  value={qtyAccepted}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setQtyAccepted(val);
                    setQtyRejected(inspection.quantity_to_inspect - val);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qty_reject">Qty Rejected</Label>
                <Input
                  id="qty_reject"
                  type="number"
                  min="0"
                  max={inspection.quantity_to_inspect}
                  value={qtyRejected}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setQtyRejected(val);
                    setQtyAccepted(inspection.quantity_to_inspect - val);
                  }}
                />
              </div>
            </div>
          )}
          
          {/* CoC Verification */}
          {inspection.coc_required && (
            <div className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Checkbox
                id="coc_verified"
                checked={cocVerified}
                onCheckedChange={setCocVerified}
              />
              <Label htmlFor="coc_verified" className="cursor-pointer text-blue-800">
                Certificate of Conformance (CoC) verified
              </Label>
            </div>
          )}
          
          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="insp_notes">Inspection Notes</Label>
            <Textarea
              id="insp_notes"
              placeholder="Document inspection findings..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
          
          {/* Warning for rejection */}
          {(result === 'FAIL' || (result === 'PARTIAL' && qtyRejected > 0)) && (
            <div className="space-y-3">
              {/* Rejection Reason Dropdown */}
              <div className="space-y-2">
                <Label htmlFor="rejection_reason">Rejection Reason *</Label>
                <Select value={rejectionReason} onValueChange={setRejectionReason}>
                  <SelectTrigger id="rejection_reason" className="border-red-300">
                    <SelectValue placeholder="Select rejection reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {REJECTION_REASONS.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Rejected quantity will be quarantined and NCR will be auto-created per AS9100D 8.7
                </p>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={validateAndProceed} 
            disabled={loading}
            className={
              result === 'PASS' ? 'bg-green-600 hover:bg-green-700' :
              result === 'FAIL' ? 'bg-red-600 hover:bg-red-700' :
              'bg-amber-600 hover:bg-amber-700'
            }
            data-testid="inspection-complete-btn"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {result === 'PASS' ? 'Release to Stock' : result === 'FAIL' ? 'Reject & Quarantine' : 'Complete Inspection'}
          </Button>
        </DialogFooter>
      </DialogContent>
      
      {/* E-Signature Dialog */}
      <ESignatureDialog
        open={showESignature}
        onOpenChange={setShowESignature}
        onConfirm={handleESignatureConfirm}
        title="QC Inspection E-Signature"
        description="AS9100D 7.5 requires documented evidence of inspection completion"
        actionDescription={
          result === 'PASS' 
            ? `Release ${inspection?.quantity_to_inspect} ${inspection?.uom || 'EA'} of ${inspection?.hpn_display} to stock`
            : result === 'FAIL'
            ? `Reject and quarantine ${qtyRejected} ${inspection?.uom || 'EA'} of ${inspection?.hpn_display}`
            : `Accept ${qtyAccepted} and quarantine ${qtyRejected} ${inspection?.uom || 'EA'} of ${inspection?.hpn_display}`
        }
        submitLabel={result === 'PASS' ? 'Sign & Release' : 'Sign & Complete'}
        isLoading={loading}
      />
    </Dialog>
  );
};

/**
 * GRN Detail View
 */
const GRNDetailView = ({ grn, onUpdate, onClose }) => {
  const [showAddLine, setShowAddLine] = useState(false);
  const [showFODDialog, setShowFODDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentGrn, setCurrentGrn] = useState(grn);
  const [loading, setLoading] = useState(true);

  const refreshGrn = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/v1/inventory/v2/grn/${grn.grn_id}`);
      setCurrentGrn(response.data);
      onUpdate(response.data);
    } catch (error) {
      console.error('Error refreshing GRN:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load full GRN with lines on mount
  useEffect(() => {
    refreshGrn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grn.grn_id]);

  const handleLineAdded = (line) => {
    refreshGrn();
    setRefreshKey(k => k + 1);
  };

  const handleReceiptComplete = (updatedGrn) => {
    setCurrentGrn(updatedGrn);
    onUpdate(updatedGrn);
  };

  return (
    <div className="space-y-6" key={refreshKey}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-[#101957]">{currentGrn.grn_number}</h2>
            <StatusBadge status={currentGrn.status} />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Created {formatIST(currentGrn.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {currentGrn.status === 'DRAFT' && currentGrn.lines?.length > 0 && (
            <Button onClick={() => setShowFODDialog(true)} className="bg-green-600 hover:bg-green-700" data-testid="grn-complete-receipt-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              Complete Receipt
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
      
      {/* GRN Info Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-2">
              <FileText className="w-4 h-4" />
              <span className="text-sm">Purchase Order</span>
            </div>
            <p className="font-semibold">{currentGrn.po_number || 'N/A'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-2">
              <Building2 className="w-4 h-4" />
              <span className="text-sm">Supplier</span>
            </div>
            <p className="font-semibold">{currentGrn.supplier_name || 'N/A'}</p>
            {currentGrn.is_approved_supplier && (
              <Badge variant="outline" className="mt-1 text-xs bg-green-50 text-green-700">
                <Check className="w-3 h-3 mr-1" />
                Approved
              </Badge>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-2">
              <Truck className="w-4 h-4" />
              <span className="text-sm">Shipment</span>
            </div>
            <p className="font-semibold">
              {currentGrn.carrier ? `${currentGrn.carrier}: ${currentGrn.tracking_number || 'N/A'}` : 'N/A'}
            </p>
            {currentGrn.packing_slip_number && (
              <p className="text-xs text-gray-500 mt-1">PS: {currentGrn.packing_slip_number}</p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* FOD Status */}
      {currentGrn.fod_verified && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Shield className="w-5 h-5 text-green-600" />
          <span className="text-green-800 font-medium">FOD Verification Passed</span>
          <span className="text-green-600 text-sm ml-auto">
            {formatIST(currentGrn.fod_verified_at)}
          </span>
        </div>
      )}
      
      {/* Lines */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Box className="w-5 h-5" />
              Receipt Lines
              <Badge variant="outline" className="ml-2">{currentGrn.lines?.length || 0}</Badge>
            </CardTitle>
            {currentGrn.status === 'DRAFT' && (
              <Button size="sm" onClick={() => setShowAddLine(true)} data-testid="grn-add-line-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Line
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!currentGrn.lines || currentGrn.lines.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Box className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No lines added yet</p>
              {currentGrn.status === 'DRAFT' && (
                <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddLine(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add First Line
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Part</TableHead>
                  <TableHead>Lot</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Accepted</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead>CoC</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentGrn.lines.map((line) => {
                  const daysToExpiry = getDaysToExpiry(line.expiry_date);
                  const expiryColor = getExpiryBadgeColor(daysToExpiry);
                  
                  return (
                    <TableRow key={line.line_id}>
                      <TableCell className="font-medium">{line.line_number}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{line.hpn_display}</p>
                          <p className="text-xs text-gray-500 truncate max-w-48">{line.part_description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-mono text-sm">{line.lot_number || 'N/A'}</p>
                          {line.supplier_lot_number && (
                            <p className="text-xs text-gray-500">Supplier: {line.supplier_lot_number}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{line.quantity_received}</TableCell>
                      <TableCell className="text-right">
                        <span className={line.quantity_accepted > 0 ? 'text-green-600 font-medium' : ''}>
                          {line.quantity_accepted}
                        </span>
                        {line.quantity_rejected > 0 && (
                          <span className="text-red-600 ml-1">({line.quantity_rejected} rej)</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {line.expiry_date ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className={`text-xs ${expiryColor}`}>
                                  {daysToExpiry !== null ? (
                                    daysToExpiry < 0 ? 'Expired' :
                                    daysToExpiry === 0 ? 'Today' :
                                    `${daysToExpiry}d`
                                  ) : '-'}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Expiry: {formatIST(line.expiry_date, { includeTime: false })}</p>
                                {daysToExpiry !== null && (
                                  <p className="text-xs">{daysToExpiry < 0 ? `Expired ${Math.abs(daysToExpiry)} days ago` : `${daysToExpiry} days remaining`}</p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {line.coc_file_id ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300">
                                  <FileText className="w-3 h-3 mr-1" />
                                  ✓
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>CoC Uploaded</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : line.coc_pending ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>CoC upload pending</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : line.requires_coc ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-300">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Missing
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>CoC required but not uploaded</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={line.status} />
                          {line.requires_inspection && line.status === 'PENDING' && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <ClipboardCheck className="w-4 h-4 text-amber-500" />
                                </TooltipTrigger>
                                <TooltipContent>Requires QC Inspection</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Notes */}
      {currentGrn.notes && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 mb-1">Notes</p>
            <p>{currentGrn.notes}</p>
          </CardContent>
        </Card>
      )}
      
      {/* Dialogs */}
      <AddLineDialog
        open={showAddLine}
        onOpenChange={setShowAddLine}
        grnId={currentGrn.grn_id}
        onAdded={handleLineAdded}
      />
      
      <FODVerificationDialog
        open={showFODDialog}
        onOpenChange={setShowFODDialog}
        grn={currentGrn}
        onComplete={handleReceiptComplete}
      />
    </div>
  );
};

/**
 * QC Queue Tab
 */
const QCQueueTab = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  const loadInspections = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/inventory/v2/inspections?page_size=100');
      setInspections(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load inspection queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInspections();
  }, [loadInspections]);

  const handleInspectionComplete = () => {
    loadInspections();
    setSelectedInspection(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ComplianceBadge clause="8.4.2" description="Verification of Externally Provided Products" />
          <ComplianceBadge clause="8.6" description="Release of Products and Services" />
        </div>
        <Button variant="outline" size="sm" onClick={loadInspections}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {inspections.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No pending inspections</p>
          <p className="text-sm">New inspections will appear here when GRNs are received</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {inspections.map((insp) => (
            <Card 
              key={insp.inspection_id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {
                setSelectedInspection(insp);
                setShowCompleteDialog(true);
              }}
              data-testid={`inspection-card-${insp.inspection_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${
                      insp.priority === 'HIGH' ? 'bg-red-100' :
                      insp.priority === 'URGENT' ? 'bg-red-200' :
                      'bg-blue-100'
                    }`}>
                      <ClipboardCheck className={`w-5 h-5 ${
                        insp.priority === 'HIGH' || insp.priority === 'URGENT' ? 'text-red-600' : 'text-blue-600'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-[#101957]">{insp.inspection_number}</p>
                        <StatusBadge status={insp.status} />
                        {insp.priority && insp.priority !== 'NORMAL' && (
                          <Badge variant="destructive" className="text-xs">{insp.priority}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{insp.hpn_display}</p>
                      <p className="text-xs text-gray-500">{insp.part_description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{insp.quantity_to_inspect}</p>
                    <p className="text-xs text-gray-500">{insp.uom || 'EA'}</p>
                    {insp.due_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Due: {formatIST(insp.due_date, { includeTime: false })}
                      </p>
                    )}
                  </div>
                </div>
                
                <Separator className="my-3" />
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4 text-gray-500">
                    <span className="flex items-center gap-1">
                      <Hash className="w-4 h-4" />
                      {insp.lot_number}
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {insp.grn_number}
                    </span>
                    {insp.supplier_name && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {insp.supplier_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {insp.coc_required && (
                      <Badge variant="outline" className="text-xs">CoC Required</Badge>
                    )}
                    <Button size="sm" variant="outline">
                      <Eye className="w-4 h-4 mr-1" />
                      Inspect
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      <CompleteInspectionDialog
        open={showCompleteDialog}
        onOpenChange={setShowCompleteDialog}
        inspection={selectedInspection}
        onComplete={handleInspectionComplete}
      />
    </div>
  );
};

/**
 * Quarantine Tab
 */
const QuarantineTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/inventory/v2/quarantine?page_size=100');
      setItems(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load quarantine items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <ComplianceBadge clause="8.7" description="Control of Nonconforming Outputs" />
        <Button variant="outline" size="sm" onClick={loadItems}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No items in quarantine</p>
          <p className="text-sm">Rejected items from QC will appear here</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((item) => (
            <Card 
              key={item.quarantine_id}
              className={`border-l-4 ${
                item.is_counterfeit_suspect ? 'border-l-red-500' :
                item.regulatory_hold ? 'border-l-purple-500' :
                'border-l-amber-500'
              }`}
              data-testid={`quarantine-card-${item.quarantine_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[#101957]">{item.quarantine_number}</p>
                      <StatusBadge status={item.status} />
                      {item.is_counterfeit_suspect && (
                        <Badge variant="destructive" className="text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Counterfeit Suspect
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{item.hpn_display}</p>
                    <p className="text-xs text-gray-500">Lot: {item.lot_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-red-600">{item.quantity}</p>
                    <p className="text-xs text-gray-500">
                      {item.days_in_quarantine} days in quarantine
                    </p>
                  </div>
                </div>
                
                <Separator className="my-3" />
                
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <span className="text-gray-500">Reason: </span>
                    <span className="font-medium">{item.quarantine_reason?.replace(/_/g, ' ')}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {item.disposition_type || 'Pending Disposition'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Main GRN Management Page
 */
const GRNManagementPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'grn');
  
  // GRN State
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrn, setSelectedGrn] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Enterprise Filters State (Phase 3)
  const [filters, setFilters] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [filterSummary, setFilterSummary] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  
  // View Mode State (Phase 4 - Table View)
  const [viewMode, setViewMode] = useState(() => getSavedViewMode());
  const [selectedGrnIds, setSelectedGrnIds] = useState([]);

  // Load suppliers for filter dropdown
  const loadSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/inventory/v2/grn/suppliers');
      setSuppliers(response.data.suppliers || []);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    }
  }, []);

  // Load GRNs with advanced filters
  const loadGrns = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params from filters
      const params = new URLSearchParams();
      params.append('page_size', '100');
      
      if (filters.status) params.append('status', filters.status);
      if (filters.supplier_name) params.append('supplier_name', filters.supplier_name);
      if (filters.po_number) params.append('po_number', filters.po_number);
      if (filters.grn_search) params.append('grn_search', filters.grn_search);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (filters.aging) params.append('aging', filters.aging);
      if (filters.has_exceptions) params.append('has_exceptions', 'true');
      
      const response = await api.get(`/api/v1/inventory/v2/grn?${params.toString()}`);
      setGrns(response.data.items || []);
      setTotalCount(response.data.total_count || 0);
      setFilterSummary(response.data.filter_summary || {});
    } catch (error) {
      toast.error('Failed to load GRNs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadGrns();
  }, [loadGrns]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  const handleGrnCreated = async (grn) => {
    // First refresh the list, then navigate to detail view
    await loadGrns();
    setSelectedGrn(grn);
  };

  const handleGrnUpdate = (updatedGrn) => {
    setGrns(grns.map(g => g.grn_id === updatedGrn.grn_id ? updatedGrn : g));
  };

  // Handle closing detail view - refresh list and clear selection
  const handleCloseDetail = () => {
    setSelectedGrn(null);
    loadGrns(); // Refresh the list when returning from detail view
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  if (selectedGrn) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <GRNDetailView
          grn={selectedGrn}
          onUpdate={handleGrnUpdate}
          onClose={handleCloseDetail}
        />
      </div>
    );
  }

  return (
    <InventoryLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#101957]">Goods Receipt & QC</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage incoming receipts, inspections, and quarantine
            </p>
          </div>
        <div className="flex items-center gap-2">
          <ComplianceBadge clause="8.4.2" description="External Provider Control" />
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-grn-btn">
            <Plus className="w-4 h-4 mr-2" />
            New GRN
          </Button>
        </div>
      </div>
      
      {/* Dashboard Header - Phase 2 Enterprise Upgrade */}
      <GRNDashboardHeader onRefresh={loadGrns} />
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="grn" className="flex items-center gap-2" data-testid="tab-grn">
            <Package className="w-4 h-4" />
            GRN
          </TabsTrigger>
          <TabsTrigger value="qc" className="flex items-center gap-2" data-testid="tab-qc">
            <ClipboardCheck className="w-4 h-4" />
            QC Queue
          </TabsTrigger>
          <TabsTrigger value="quarantine" className="flex items-center gap-2" data-testid="tab-quarantine">
            <AlertTriangle className="w-4 h-4" />
            Quarantine
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="grn" className="space-y-4">
          {/* Enterprise Filters - Phase 3 */}
          <GRNFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            suppliers={suppliers}
            loading={loading}
            filterSummary={filterSummary}
          />
          
          {/* Results Summary with View Toggle */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>
              {totalCount} GRN{totalCount !== 1 ? 's' : ''} found
              {Object.keys(filters).length > 0 && ' (filtered)'}
            </span>
            <div className="flex items-center gap-3">
              {/* View Toggle - Phase 4 */}
              <ViewToggle
                viewMode={viewMode}
                onViewModeChange={(mode) => {
                  setViewMode(mode);
                  setSelectedGrnIds([]); // Clear selection on view change
                }}
              />
              <Button variant="ghost" size="sm" onClick={loadGrns}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* GRN List - Card or Table View */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : grns.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No GRNs found</p>
              {Object.keys(filters).length > 0 ? (
                <Button 
                  variant="outline" 
                  className="mt-3" 
                  onClick={() => setFilters({})}
                >
                  Clear Filters
                </Button>
              ) : (
                <Button variant="outline" className="mt-3" onClick={() => setShowCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Create First GRN
                </Button>
              )}
            </div>
          ) : viewMode === 'table' ? (
            /* Table View - Phase 4 */
            <GRNTableView
              grns={grns}
              onSelect={setSelectedGrn}
              selectedIds={selectedGrnIds}
              onSelectionChange={setSelectedGrnIds}
              loading={loading}
            />
          ) : (
            /* Card View - Original */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {grns.map((grn) => (
                <GRNCard
                  key={grn.grn_id}
                  grn={grn}
                  onClick={setSelectedGrn}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="qc">
          <QCQueueTab />
        </TabsContent>
        
        <TabsContent value="quarantine">
          <QuarantineTab />
        </TabsContent>
      </Tabs>
      
      {/* Create GRN Dialog */}
      <CreateGRNDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={handleGrnCreated}
      />
      </div>
    </InventoryLayout>
  );
};

export default GRNManagementPage;
